import { __decorate, __metadata } from '../../node_modules/tslib/tslib.es6.js';
import '../../node_modules/lit/node_modules/@lit/reactive-element/reactive-element.js';
import { html as x } from '../../node_modules/lit-html/lit-html.js';
import '../../node_modules/lit-element/lit-element.js';
import { property as n } from '../../node_modules/lit/node_modules/@lit/reactive-element/decorators/property.js';
import { state as t } from '../../node_modules/lit/node_modules/@lit/reactive-element/decorators/state.js';
import '../../node_modules/lit/node_modules/@lit/reactive-element/decorators/query-assigned-elements.js';
import { ifDefined as l } from '../../node_modules/lit-html/directives/if-defined.js';
import getClassName from '../../node_modules/@fmr-ap142296/brick-helpers/dist/src/functions/get-class-name.js';
import { createPopper } from '../../node_modules/@popperjs/core/dist/esm/popper.js';
import { ScopedElementsMixin } from '../utilities/scoped-elements/scoped-elements.mixin.js';
import { PvdSlotifyBaseComponent } from '../pvd-base-component/pvd-slotify-base-component.js';
import { KeyboardKey, LegacyKeyboardKey } from '../pvd-utils/enums/keyboard-key.js';
import { LayerPosition } from '../pvd-utils/enums/layer-position.js';
import { MutationType } from '../pvd-utils/enums/mutation-type.js';
import { FOCUSABLE_SELECTORS } from '../pvd-utils/constants/focusable-selectors.js';
import { getElement } from '../pvd-utils/scripts/get-element.js';
import { getCloseIfTriggerHiddenPopperModifier } from '../pvd-utils/scripts/get-close-if-trigger-hidden-popper-modifier.js';
import { getCurrentPlacementPopperModifier } from '../pvd-utils/scripts/get-current-placement-popper-modifier.js';
import { getZIndexPopperModifier } from '../pvd-utils/scripts/get-z-index-popper-modifier.js';

const DEFAULT_BOUNDARY = 'clippingParents';
const DEFAULT_DISABLE_FOCUS = false;
const DEFAULT_DISABLE_RETURN_FOCUS = false;
const DEFAULT_DISABLE_OVERFLOW = false;
const DEFAULT_PLACEMENT = LayerPosition.top;
const DEFAULT_OPEN = false;
const DEFAULT_CLOSE_BTN_A11Y_TEXT = 'Close';
const DEFAULT_TRIGGER_TEXT_CONTENT = 'more information';
const DEFAULT_DISABLE_TRIGGER_SLOT_BUTTON = false;
/**
 * Providence Popover component.
 * @see {@link https://providence.fmr.com/4.x/components/popover/popover.html|Popover documentation}
 */
class PvdPopover extends ScopedElementsMixin(PvdSlotifyBaseComponent) {
    constructor() {
        super(...arguments);
        // function to call when user clicks outside Popover
        this.onClickOutside = (e) => (this.pvdOpen = false);
        /**
         * Sets the `aria-label` value for the popover element (<div role="dialog">).
         * This is defaulted to the text content of the trigger slot so that the popover always has an acceesible name.
         * It can be customized by consumers if needed.
         */
        this.pvdAriaLabel = DEFAULT_TRIGGER_TEXT_CONTENT;
        /**
         * Prevents the focus being forced onto the popover when opened
         */
        this.pvdDisableFocus = DEFAULT_DISABLE_FOCUS;
        /**
         * Prevents the focus from returning to the trigger when the popover closes
         */
        this.pvdDisableReturnFocus = DEFAULT_DISABLE_RETURN_FOCUS;
        /**
         * Prevents the overflow mutation observer from being created or destroyed
         */
        this.pvdDisableOverflow = DEFAULT_DISABLE_OVERFLOW;
        /**
         * Shows/Hides the popover element
         */
        this.pvdOpen = DEFAULT_OPEN;
        /**
         * Set the `aria-label` value of the slotted trigger button
         */
        this.pvdTriggerButtonA11yText = DEFAULT_TRIGGER_TEXT_CONTENT;
        /**
         * Placement of the popover
         */
        this.pvdPlacement = DEFAULT_PLACEMENT;
        // #endregion
    }
    static get scopedElements() {
        // $namespace-$name-$extendingComponentName
        return {
            'pvd-scoped-icon': customElements.get(`${this.namespace}-icon`),
        };
    }
    // #endregion
    // #region protected methods
    /**
     * Determines if the consumer provided a slotted trigger
     */
    get hasTrigger() {
        const isTriggerSlotUsed = this.isSlotUsed(`trigger`);
        // if trigger slot is used, this property must be set to true
        // this prevents consumers from forgetting to set it via the attribute
        this.pvdDisableTriggerSlotButton = isTriggerSlotUsed;
        return isTriggerSlotUsed;
    }
    // #endregion
    // #region private methods
    /**
     * Generates a unique ID if one is not provided within the component
     */
    get generatedPopoverId() {
        if (!this._generatedPopoverId) {
            // Id must start with a letter
            this._generatedPopoverId = `pvd-id-${PvdSlotifyBaseComponent.generateRandomNumber()}`;
        }
        return this._generatedPopoverId;
    }
    /**
     * Returns the element reference that was either provided through the pvdBoundary prop, or the result of a querySelector whos value was this.pvdBoundary
     */
    get boundary() {
        return getElement(this.pvdBoundary) || DEFAULT_BOUNDARY;
    }
    /**
     * Returns the element reference that was either provided through the pvdTarget prop, or the result of a querySelector whos value was this.pvdTarget
     */
    get target() {
        return getElement(this.pvdTarget);
    }
    /**
     * Returns an array of the modifiers used to configure PopperJS instance
     */
    get popperJsModifiers() {
        const currentPlacementModifier = getCurrentPlacementPopperModifier.call(this);
        const zIndexModifier = getZIndexPopperModifier.call(this);
        const closeIfTriggerHiddenModifier = getCloseIfTriggerHiddenPopperModifier.call(this, this.constructor.namespace);
        return [
            currentPlacementModifier,
            zIndexModifier,
            closeIfTriggerHiddenModifier,
            {
                name: 'flip',
                options: {
                    fallbackPlacements: ['top', 'right', 'bottom', 'left'],
                    boundary: this.boundary,
                },
            },
            {
                name: 'offset',
                options: {
                    offset: [0, 10],
                },
            },
            {
                name: 'arrow',
                options: {
                    element: this._arrowElement,
                    padding: 24,
                },
            },
            {
                name: 'preventOverflow',
                options: {
                    altAxis: true,
                    boundary: this.boundary,
                },
            },
        ];
    }
    /**
     * Callback when 'pvdOpen' changes
     */
    async openStateChange() {
        await this.updateComplete;
        if (this.pvdOpen) {
            // adds init values for observed
            this.isContentOverflowing();
            this.addFirstLastFocusableElements();
            // if "external target (not slotted)", we manage focus
            this.focusPopover();
            // add the click outside listener
            this.addClickOutsideListener();
            // Asynchronously updates the popper instance, and returns a promise. Use this for high-frequency updates.
            // fire the open event
            this.popper.update();
            this.dispatchEvent(new CustomEvent('pvd-popover-change', { detail: { open: true } }));
            // popperjs traps events on the trigger so we can use this observer handler to track which popover is open
            if (!!PvdPopover.openPopover && PvdPopover.openPopover !== this) {
                PvdPopover.openPopover.pvdOpen = false;
            }
            PvdPopover.openPopover = this;
        }
        else {
            // remove the click outside listener
            this.removeClickOutsideListener();
            // move focus back to the triggering element
            this.returnFocus();
            // remove references to focusable elements
            this.removeFirstLastFocusableElements();
            // fire the close event
            this.dispatchEvent(new CustomEvent('pvd-popover-change', { detail: { open: false } }));
            // popperjs traps events on the trigger so we can use this observer handler to track which popover is open
            if (PvdPopover.openPopover === this) {
                PvdPopover.openPopover = undefined;
            }
        }
    }
    /**
     * Sets the boundary element for the PopperJS 'preventOverflow' and 'flip' modifiers
     */
    async boundaryStateChange() {
        if (!this.popper) {
            return;
        }
        this.popper.setOptions({ modifiers: this.popperJsModifiers });
        await this.popper.update();
    }
    /**
     * Callback method when 'pvdPlacement' is changed
     */
    async placementStateChange() {
        if (!this.popper) {
            return;
        }
        this.popper.setOptions({ placement: this.pvdPlacement });
        await this.popper.update();
    }
    /**
     * Callback method when 'pvdTarget' is changed
     */
    async targetStateChange() {
        // if a popper instance exists, we need to tear down before setting back up
        if (this.popper) {
            this.removeMutationObservers();
            this.removePopperInstance();
        }
        // set up new element references, new popper instance, and new mutation observer
        this.addElementReferences();
        this.addPopperInstance();
    }
    /**
     * Sets the zIndex modifier for PopperJS instance
     */
    async zIndexStateChange() {
        if (!this.popper) {
            return;
        }
        this.popper.setOptions({ modifiers: this.popperJsModifiers });
        await this.popper.update();
    }
    /**
     * Returns and caches a private reference to a generated slot area ID.
     */
    generateSlotAreaId() {
        if (!this._generatedSlotAreaId) {
            this._generatedSlotAreaId = `pvd-popover-slot-area-id-${PvdSlotifyBaseComponent.generateRandomNumber()}`;
        }
        return this._generatedSlotAreaId;
    }
    /**
     * Sets focus on the close button
     */
    async focusPopover() {
        // wait for things to update and chill within PopperJS
        await this.updateComplete;
        if (!this.pvdDisableFocus && this._popoverElement) {
            this._firstFocusablePopoverEl.focus();
        }
    }
    /**
     * Returns focus to the previously focused element before the popover was opened
     */
    async returnFocus() {
        // wait for things to update and chill within PopperJS
        await this.updateComplete;
        // keep focus on poover 
        if (!this.pvdDisableFocus && this._popoverElement) {
            return;
        }
        else if (!this.pvdDisableReturnFocus && this._returnFocusElement) {
            this._returnFocusElement.focus();
        }
    }
    /**
     * Captures the internal references for the popover elements
     */
    addElementReferences() {
        if (this.hasTrigger) {
            const triggerSlot = this.querySelector(`s-slot`);
            this._triggerElement = this.pvdDisableTriggerSlotButton
                ? triggerSlot.querySelector(FOCUSABLE_SELECTORS)
                : this.querySelector(`.pvd-popover__trigger-wrapper`);
        }
        else {
            this._triggerElement = this.target;
        }
        this._popoverElement = this.querySelector(`.pvd-popover__wrapper`);
        this._arrowElement = this._popoverElement.querySelector(`.pvd-popover__arrow`);
    }
    /**
     * Initializes the popperjs instance and merges user provided options
     */
    addPopperInstance() {
        // no trigger, no popper
        if (!this._triggerElement) {
            console.warn(`PVD Popover: no trigger slot or \`pvd-target\` specified for '${this}'`);
            return;
        }
        // popper.js
        if (!this.popper) {
            this.popper = createPopper(this._triggerElement, this._popoverElement, {
                placement: this.pvdPlacement,
                modifiers: this.popperJsModifiers,
            });
        }
    }
    /**
     * Tears down the popperjs instance and sets the internal reference to `undefined`
     */
    removePopperInstance() {
        if (this.popper) {
            this.popper.destroy();
            this.popper = undefined;
        }
    }
    /**
     * Sets the first and last focusable elements within the popover
     */
    addFirstLastFocusableElements() {
        // do NOT add await this.updateComplete in this method. IE11 cannot handle the power
        // capture the currently active element, so that we can return to it on close
        this._returnFocusElement = this._triggerElement;
        const preliminaryFocusableEls = Array.from(this._popoverElement.querySelectorAll(FOCUSABLE_SELECTORS));
        // checks to see if any preliminary focusable el has or is within a display:none or visibility:hidden
        const focusableElements = preliminaryFocusableEls.filter((currEl) => {
            return (currEl.offsetHeight > 0 && getComputedStyle(currEl).visibility.toUpperCase() !== 'HIDDEN');
        });
        if (focusableElements) {
            this._firstFocusablePopoverEl = focusableElements[0];
            this._lastFocusablePopoverEl = focusableElements[focusableElements.length - 1];
        }
    }
    /**
     * Unsets the first and last focusable elements within the popover
     */
    removeFirstLastFocusableElements() {
        this._firstFocusablePopoverEl = this._lastFocusablePopoverEl = null;
    }
    /**
     * Adds the click handler outside the popover
     */
    addClickOutsideListener() {
        this._boundClickOutsideHandler = this.clickOutsideEventHandler.bind(this);
        document.body.addEventListener('click', this._boundClickOutsideHandler);
    }
    /**
     * Removes the click handler outside the popover
     */
    removeClickOutsideListener() {
        document.body.removeEventListener('click', this._boundClickOutsideHandler);
        this._boundClickOutsideHandler = undefined;
    }
    /**
     * Creates mutation observer to watch for content changing within the Popover content area
     */
    addContentChangeObserver() {
        if (!this._contentChangeObserver) {
            this._contentChangeObserver = new MutationObserver((mutationsList) => {
                this.contentChangeMutationEventHandler(mutationsList);
            });
        }
    }
    /**
     * Starts observations for changes on elements
     */
    observeMutations() {
        // content overflow observer
        if (this._contentChangeObserver && this._popoverElement) {
            this._contentChangeObserver.observe(this._popoverElement, {
                attributes: true,
                childList: true,
                characterData: true,
                subtree: true,
            });
        }
    }
    /**
     * Removes the mutation observers within Popover
     */
    removeMutationObservers() {
        // content overflow observer
        if (this._contentChangeObserver) {
            this._contentChangeObserver.disconnect();
        }
    }
    /**
     * Inspects the bounding boxes of the content and it's wrapper to determine if the content is scrolling
     */
    isContentOverflowing() {
        const slotArea = this.querySelector('.pvd-popover__slot-area');
        // needing to round due to IE11 miscalculations
        const slotScrollHeight = Math.round(slotArea === null || slotArea === void 0 ? void 0 : slotArea.scrollHeight);
        const wrapperRectHeight = Math.round(slotArea === null || slotArea === void 0 ? void 0 : slotArea.getBoundingClientRect()['height']);
        // check if the slotted content is taller than the bounding box
        if (slotScrollHeight && wrapperRectHeight) {
            this.pvdContentOverflow = slotScrollHeight > wrapperRectHeight;
        }
    }
    // #endregion
    // #region events
    /**
     * Handles Popover trigger element mouse events (for slotted triggers)
     * @param e Mouse event object
     */
    async triggerEventHandler(e) {
        e.stopPropagation();
        e.preventDefault();
        // wait for things to update and chill within PopperJS
        await this.updateComplete;
        this.pvdOpen = !this.pvdOpen;
    }
    async triggerKeyHandler(e) {
        const isSpacePressed = e.key === KeyboardKey.space || e.key === LegacyKeyboardKey.space;
        const isEnterPressed = e.key === KeyboardKey.enter || e.key === LegacyKeyboardKey.enter;
        if (isSpacePressed || isEnterPressed) {
            e.stopPropagation();
            e.preventDefault();
            // wait for things to update and chill within PopperJS
            await this.updateComplete;
            this.pvdOpen = true;
        }
    }
    /**
     * Handles close button mouse events
     * @param e Mouse event object
     */
    closeButtonEventHandler(e) {
        e.stopPropagation();
        e.preventDefault();
        this.pvdOpen = false;
    }
    /**
     * Ensures that when the popover is open, the only tabbable elements are witihin the popover
     * @param e, keyboard event
     */
    keyboardEventHandler(e) {
        const isTabPressed = e.key === KeyboardKey.tab || e.key === LegacyKeyboardKey.tab;
        const isEscapePressed = e.key === KeyboardKey.escape || e.key === LegacyKeyboardKey.escape;
        // TAB or SHIFT+TAB
        if (isTabPressed) {
            if (e.shiftKey) {
                // if shift key pressed for shift + tab combination
                if (document.activeElement === this._firstFocusablePopoverEl) {
                    this._lastFocusablePopoverEl.focus();
                    e.preventDefault();
                }
            }
            else {
                // if tab key is pressed
                if (document.activeElement === this._lastFocusablePopoverEl) {
                    this._firstFocusablePopoverEl.focus();
                    e.preventDefault();
                    // if focused has reached to last focusable element then focus first focusable element after pressing tab
                }
            }
        }
        // ESCAPE KEY
        if (isEscapePressed) {
            this.pvdOpen = false;
        }
    }
    /**
     * Handles clicks outside the popover or popover trigger
     * @param e Mouse event object
     */
    clickOutsideEventHandler(e) {
        if (!this._popoverElement.contains(e.target) &&
            !this._triggerElement.contains(e.target)) {
            // prevent calling the function if the `onClickOutside` has been set to `undefined`
            if (typeof this.onClickOutside !== 'undefined') {
                this.onClickOutside(e);
            }
        }
    }
    /**
     * Mutation Observer callback for when content within Popover gets changed
     *
     * @param mutationsList
     * @param observer
     */
    contentChangeMutationEventHandler(mutationsList) {
        const mutations = mutationsList.filter((mutation) => {
            // needed in order to support the change of an element that updates to display:none
            const validAttributeMutation = mutation.type === MutationType.attributes && mutation.attributeName === 'style';
            return (this.pvdOpen &&
                (mutation.type === MutationType.characterData ||
                    mutation.type === MutationType.childList ||
                    validAttributeMutation));
        });
        if (mutations.length > 0) {
            this.isContentOverflowing();
            this.addFirstLastFocusableElements();
        }
    }
    // #endregion
    // #region LitElement lifecycles
    updated(changedProperties) {
        super.updated(changedProperties);
        if (changedProperties.has('pvdOpen')) {
            this.openStateChange();
        }
        if (changedProperties.has('pvdBoundary')) {
            this.boundaryStateChange();
        }
        if (changedProperties.has('pvdPlacement')) {
            this.placementStateChange();
        }
        if (changedProperties.has('pvdTarget')) {
            this.targetStateChange();
        }
        if (changedProperties.has('pvdZIndex')) {
            this.zIndexStateChange();
        }
    }
    async firstUpdated() {
        super.firstUpdated();
        // create observers if they don't exist
        this.addContentChangeObserver();
        // Popper.js sets global events that need Popover references;
        // wait for component to full update before setup
        await this.updateComplete;
        // create references
        this.addElementReferences();
        // observe mutation observer
        this.observeMutations();
        // create popper instance
        this.addPopperInstance();
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        this.removeMutationObservers();
        this.removePopperInstance();
    }
    async _getUpdateComplete() {
        const popperUpdate = this.popper ? this.popper.update() : undefined;
        return Promise.all([super._getUpdateComplete(), popperUpdate]);
    }
    renderTrigger() {
        // this simply adds title accessibility text to the underlying trigger link
        // as nice as it would be, it is not possible to simply pass the attribute in to the slot
        this.addEventListener('slotchange', e => {
            // assignedWrapper comes from ApexSlots, need type definition (export ApexSlot class, most likely)
            // https://github.com/Fidelity-Green/apex-for-web/blob/cfdb7f3e692f897d486dca38bdeee4cd2ea76448/utilities/slots/lib/utils.ts#L25
            var _a;
            const slot = e.target; // TODO: cast as ApexSlot when we expose the type definition
            if (slot.name === 'trigger') {
                const triggerElement = (_a = slot.assignedWrapper) === null || _a === void 0 ? void 0 : _a.firstChild;
                if (!triggerElement) {
                    return;
                }
                // we will always set the aria-haspopup and aria-label attributes on the trigger even if the consumer provides one
                triggerElement.pvdNativeButtonAttributes = { 'aria-haspopup': 'dialog' };
                triggerElement.pvdNativeAnchorAttributes = { 'aria-haspopup': 'dialog' };
                // button and link expose an attribute to set aria-label so we use that attribute instead of the native attributes API
                triggerElement.pvdAriaLabel = `${this.pvdTriggerButtonA11yText}`;
            }
        });
        return x `${this.hasTrigger
            ? x `<div class="pvd-popover__trigger-wrapper" role="${l(this.pvdDisableTriggerSlotButton ? undefined : `button`)}" tabindex="${l(this.pvdDisableTriggerSlotButton ? undefined : `0`)}" @click="${this.triggerEventHandler}" @keydown="${this.triggerKeyHandler}"><s-slot name="trigger"></s-slot></div>`
            : x `<s-slot name="trigger"></s-slot>`}`;
    }
    renderCloseIcon() {
        return this.scopeTemplate `
      <div class="pvd-popover__close-button-area">
        <button
          aria-label="close"
          class="pvd-popover__close-button"
          type="button"
          @click="${this.closeButtonEventHandler}"
        >
          <pvd-scoped-icon pvd-name="action__close" pvd-size="small"></pvd-scoped-icon>
        </button>
      </div>
    `;
    }
    render() {
        const classMap = {
            default: `${this.constructor.namespace}-popover-root`,
            modifierPrefix: 'pvd-popover',
            modifiers: [
                {
                    condition: this.pvdOpen,
                    className: 'open',
                },
                {
                    condition: this.pvdContentOverflow,
                    className: 'content-overflow',
                },
                {
                    condition: this.pvdCurrentPlacement !== undefined,
                    className: `placement-${this.pvdCurrentPlacement}`,
                },
            ],
        };
        return x `<div class="${getClassName(classMap)}" @keydown="${this.keyboardEventHandler}">${this.renderTrigger()}<div aria-describedby="${l(this.pvdAriaDescribedby)}" aria-label="${l(this.pvdAriaLabelledby ? undefined : this.pvdAriaLabel)}" aria-labelledby="${l(this.pvdAriaLabelledby)}" aria-modal="true" class="pvd-popover__wrapper" id="${this.pvdId || this.generatedPopoverId}" role="dialog" tabindex="${this.pvdOpen ? '0' : '-1'}"><div class="pvd-popover__popover">${this.renderCloseIcon()}<div class="pvd-popover__slot-area" id="${l(this._generatedSlotAreaId)}" role="${l(this.pvdContentOverflow ? 'region' : undefined)}" tabindex="${l(this.pvdContentOverflow ? '0' : undefined)}"><s-slot></s-slot></div></div><span class="pvd-popover__arrow"></span></div></div>`;
    }
}
//#region Component configuration
PvdPopover.componentName = 'popover';
__decorate([
    n({ type: String, reflect: true, attribute: 'pvd-aria-describedby' }),
    __metadata("design:type", String)
], PvdPopover.prototype, "pvdAriaDescribedby", void 0);
__decorate([
    n({ type: String, reflect: true, attribute: 'pvd-aria-label' }),
    __metadata("design:type", String)
], PvdPopover.prototype, "pvdAriaLabel", void 0);
__decorate([
    n({ type: String, reflect: true, attribute: 'pvd-aria-labelledby' }),
    __metadata("design:type", String)
], PvdPopover.prototype, "pvdAriaLabelledby", void 0);
__decorate([
    n({
        type: String,
        reflect: true,
        attribute: 'pvd-boundary',
    }),
    __metadata("design:type", Object)
], PvdPopover.prototype, "pvdBoundary", void 0);
__decorate([
    n({
        type: String,
        reflect: true,
        attribute: 'pvd-close-button-a11y-text',
    }),
    __metadata("design:type", String)
], PvdPopover.prototype, "pvdCloseButtonA11yText", void 0);
__decorate([
    n({
        type: Boolean,
        reflect: false,
        attribute: 'pvd-disable-focus',
        ...PvdSlotifyBaseComponent.booleanAttribute('pvd-disable-focus'),
    }),
    __metadata("design:type", Boolean)
], PvdPopover.prototype, "pvdDisableFocus", void 0);
__decorate([
    n({
        type: Boolean,
        reflect: false,
        attribute: 'pvd-disable-return-focus',
        ...PvdSlotifyBaseComponent.booleanAttribute('pvd-disable-return-focus'),
    }),
    __metadata("design:type", Boolean)
], PvdPopover.prototype, "pvdDisableReturnFocus", void 0);
__decorate([
    n({
        type: Boolean,
        reflect: false,
        attribute: 'pvd-disable-overflow',
        ...PvdSlotifyBaseComponent.booleanAttribute('pvd-disable-overflow'),
    }),
    __metadata("design:type", Boolean)
], PvdPopover.prototype, "pvdDisableOverflow", void 0);
__decorate([
    n({
        type: Boolean,
        reflect: true,
        attribute: 'pvd-disable-trigger-slot-button',
        ...PvdSlotifyBaseComponent.booleanAttribute('pvd-disable-trigger-slot-button'),
    }),
    __metadata("design:type", Boolean)
], PvdPopover.prototype, "pvdDisableTriggerSlotButton", void 0);
__decorate([
    n({
        type: Boolean,
        reflect: true,
        attribute: 'pvd-open',
        ...PvdSlotifyBaseComponent.booleanAttribute('pvd-open'),
    }),
    __metadata("design:type", Boolean)
], PvdPopover.prototype, "pvdOpen", void 0);
__decorate([
    n({
        type: String,
        reflect: true,
        attribute: 'pvd-trigger-button-a11y-text',
    }),
    __metadata("design:type", String)
], PvdPopover.prototype, "pvdTriggerButtonA11yText", void 0);
__decorate([
    n({
        type: String,
        reflect: true,
        attribute: 'pvd-placement',
    }),
    __metadata("design:type", String)
], PvdPopover.prototype, "pvdPlacement", void 0);
__decorate([
    n({
        type: String,
        reflect: true,
        attribute: 'pvd-id',
    }),
    __metadata("design:type", String)
], PvdPopover.prototype, "pvdId", void 0);
__decorate([
    n({
        attribute: 'pvd-target',
    }),
    __metadata("design:type", Object)
], PvdPopover.prototype, "pvdTarget", void 0);
__decorate([
    n({
        type: String,
        reflect: true,
        attribute: 'pvd-z-index',
    }),
    __metadata("design:type", String)
], PvdPopover.prototype, "pvdZIndex", void 0);
__decorate([
    t(),
    __metadata("design:type", Boolean)
], PvdPopover.prototype, "pvdContentOverflow", void 0);
__decorate([
    t(),
    __metadata("design:type", String)
], PvdPopover.prototype, "pvdCurrentPlacement", void 0);

export { DEFAULT_BOUNDARY, DEFAULT_CLOSE_BTN_A11Y_TEXT, DEFAULT_DISABLE_FOCUS, DEFAULT_DISABLE_OVERFLOW, DEFAULT_DISABLE_RETURN_FOCUS, DEFAULT_DISABLE_TRIGGER_SLOT_BUTTON, DEFAULT_OPEN, DEFAULT_PLACEMENT, DEFAULT_TRIGGER_TEXT_CONTENT, PvdPopover };
//# sourceMappingURL=pvd-popover.js.map
